import { create } from 'zustand';

import type { ChatSurfaceDescriptor } from './chatDockTypes';

interface ChatDockRegistryState {
  descriptors: Record<string, ChatSurfaceDescriptor>;
  activeOwnerId: string | null;
  register: (descriptor: ChatSurfaceDescriptor) => void;
  unregister: (ownerId: string) => void;
}

export const useChatDockRegistry = create<ChatDockRegistryState>((set) => ({
  descriptors: {},
  activeOwnerId: null,
  register: (descriptor) =>
    set((state) => ({
      descriptors: { ...state.descriptors, [descriptor.ownerId]: descriptor },
      // A default ("floor") surface never becomes active; specific surfaces do.
      activeOwnerId: descriptor.isDefault ? state.activeOwnerId : descriptor.ownerId,
    })),
  unregister: (ownerId) =>
    set((state) => {
      const next = { ...state.descriptors };
      delete next[ownerId];
      if (state.activeOwnerId !== ownerId) {
        return { descriptors: next, activeOwnerId: state.activeOwnerId };
      }
      // The active (specific) surface left — fall back to another specific, else null.
      const nextSpecific = Object.values(next).find((d) => !d.isDefault)?.ownerId ?? null;
      return { descriptors: next, activeOwnerId: nextSpecific };
    }),
}));

/** Resolve the active descriptor (null when none registered). */
export function selectActiveDescriptor(
  state: Pick<ChatDockRegistryState, 'descriptors' | 'activeOwnerId'>
): ChatSurfaceDescriptor | null {
  const active = state.activeOwnerId ? state.descriptors[state.activeOwnerId] : undefined;
  if (active) {
    return active;
  }
  // No active specific surface: fall back to the registered default (floor), if any.
  return Object.values(state.descriptors).find((d) => d.isDefault) ?? null;
}
